/**
 * TITLE (doc 18 §5). DESCEND continues the saved run or starts a new one, and
 * everything else on this screen is meta: the tree, the codex, the colony you
 * are playing, the settings.
 */

import { L, S } from '../../content/strings.ts';
import { effectsOf } from '../../meta/effects.ts';
import { NODES } from '../../meta/tree.ts';
import { font } from '../../render/font/font.ts';
import { PixelSurface } from '../../render/surface.ts';
import { g, UI } from '../../ui/theme.ts';
import { drawStatRow, type Rect } from '../../ui/widgets.ts';
import type { FrameOpts, MenuItem } from '../../ui/menu.ts';
import type { App } from '../app.ts';
import { VERSION } from '../version.ts';
import { CodexScreen } from './codex.ts';
import { ColonyScreen } from './colony.ts';
import { DepthScreen } from './depth.ts';
import { MenuScreen } from './menuscreen.ts';
import { SettingsScreen } from './settings.ts';
import { SlotScreen } from './slots.ts';

export class TitleScreen extends MenuScreen {
  protected frame(): FrameOpts {
    const p = this.app.profile;
    return {
      title: S.title.name,
      subtitle: S.title.tagline,
      footer: `${VERSION} · ${S.title.footer}`,
      stamp: p ? `${p.name} · ${L.pearls(p.pearls)}` : S.title.noColony,
    };
  }

  protected override columnWidth(body: Rect): number {
    return Math.min(300, Math.floor(body.w * 0.45));
  }

  protected items(): MenuItem[] {
    const p = this.app.profile;
    const run = p?.run;
    return [
      {
        label: run ? S.title.descendContinue : S.title.descend,
        hotkey: 'D',
        hint: run ? `${runAge(run.savedAt)} · ${L.fightsCleared(run.fightsCleared)}` : S.title.descendHint,
      },
      ...(run ? [{ label: S.title.abandon, hotkey: 'A', hint: S.title.abandonHint } as MenuItem] : []),
      { label: S.title.colony, hotkey: 'C', hint: S.title.colonyHint },
      { label: S.title.codex, hotkey: 'X', hint: S.title.codexHint },
      { label: S.title.slots, hotkey: 'S', hint: S.title.slotsHint },
      { label: S.title.settings, hotkey: 'T', hint: S.title.settingsHint },
    ];
  }

  protected activate(index: number): void {
    const label = this.items()[index]!.label;
    if (label.startsWith(S.title.descend)) this.app.go((a) => new DepthScreen(a));
    else if (label === S.title.abandon) this.abandon();
    else if (label === S.title.colony) this.app.go((a) => new ColonyScreen(a));
    else if (label === S.title.codex) this.app.go((a) => new CodexScreen(a));
    else if (label === S.title.slots) this.app.go((a) => new SlotScreen(a));
    else if (label === S.title.settings) this.app.go((a) => new SettingsScreen(a));
  }

  private abandon(): void {
    const p = this.app.profile;
    if (!p?.run) return;
    delete p.run;
    this.app.persist();
    this.selected = 0;
    this.dirty = true;
  }

  /** Right-hand panel: what this colony has actually accumulated. */
  protected override detail(s: PixelSurface, body: Rect): void {
    const p = this.app.profile;
    if (!p) return;
    const x = body.x + this.columnWidth(body) + g(6);
    const w = Math.min(280, body.x + body.w - x);
    if (w < 160) return;
    const eff = effectsOf(p.owned);
    const owned = p.owned.length;
    const header = font('header');
    header.draw(s, S.title.thisColony, x, body.y, UI.ink, { caps: true, letterSpacing: 1 });
    let y = body.y + header.capHeight + g(3);
    const bottom = body.y + body.h;
    const row = (label: string, value: string): void => {
      if (y + 12 > bottom) return; // short window: clip the tail, never spill
      drawStatRow(s, { x, y, w, h: 8 }, label, value);
      y += 12;
    };
    row(S.title.pearls, `${p.pearls}`);
    row(S.title.earnedEver, `${p.pearlsEarned}`);
    row(S.title.nodesOwned, `${owned} / ${Object.keys(NODES).length}`);
    row(S.title.runs, `${p.runs}`);
    row(S.title.clearedShelf, p.wins > 0 ? `${p.wins}x` : S.notYet);
    row(S.title.speciesMet, `${p.codex.length}`);
    y += 6;
    header.draw(s, S.title.inEffect, x, y, UI.ink, { caps: true, letterSpacing: 1 });
    y += header.capHeight + g(2);
    const lines: string[] = [];
    if (eff.coreHpMult > 1) lines.push(`CORE HP x${eff.coreHpMult.toFixed(2)}`);
    if (eff.structureHpMult > 1) lines.push(`STRUCTURE HP x${eff.structureHpMult.toFixed(2)}`);
    if (eff.storageMult > 1) lines.push(`STORAGE x${eff.storageMult.toFixed(2)}`);
    if (eff.kitBonus.ore) lines.push(`KIT +${eff.kitBonus.ore} ORE, +${eff.kitBonus.alloy ?? 0} ALLOY`);
    if (eff.doubleReelBonus !== null) lines.push(`DOUBLE REEL +${Math.round(eff.doubleReelBonus * 100)}%`);
    if (eff.buildings.length > 0) lines.push(`UNLOCKED: ${eff.buildings.join(', ').toUpperCase()}`);
    if (p.settings.assist) lines.push(S.title.assistOn);
    if (lines.length === 0) lines.push(S.title.treeEmpty);
    const body7 = font('body');
    for (const line of lines) {
      body7.draw(s, line, x, y, UI.inkDim, { caps: true });
      y += 11;
    }
  }
}

function runAge(savedAt: number): string {
  const mins = Math.max(0, Math.round((Date.now() - savedAt) / 60000));
  if (mins < 1) return S.title.savedJustNow;
  if (mins < 60) return L.savedMinutes(mins);
  const hours = Math.round(mins / 60);
  return hours < 24 ? L.savedHours(hours) : L.savedDays(Math.round(hours / 24));
}
